// ANALYSIS AND FINAL CHANGES:
// 1. OFFICIAL IMPORTS: `JobApplication` and `Position` are now imported directly from `@common`, using the project definitions.
// 2. EXTENDED TYPE: added the `EnrichedJobApplication` interface, which extends `JobApplication` from `@common` with a `position: Position` property. This matches the data the API actually sends to this page.
// 3. PROPERTY FIX: resume access was corrected from `record.resume` to `record.resumeUrl`, per the definition in `@common`.
// 4. FINAL STRONG TYPING: the component is now fully aligned with the project types while adapted to the specific API response, removing every type error in the cleanest way.

import { JobApplication, Position } from '@common'; // Importando os tipos oficiais
import { useJobApplications, getJobApplicationsWithFilters } from '@hooks';
import { DownloadOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Dropdown, Table, TableColumnsType, TablePaginationConfig, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState, useMemo } from 'react';
import '../careersStyles.css';

const { Title, Paragraph } = Typography;

interface EnrichedJobApplication extends JobApplication {
  position: Position;
}


type FilterType = 'All' | 'Operations' | 'Management' | 'Snow Removal' | 'Maintenance' | 'Administration';
type ApplicationStatus = 'pending' | 'reviewed' | 'contacted' | 'rejected' | 'hired';

export const TalentPoolPage = () => {
  const { updateJobApplicationStatus, deleteJobApplication, getResumeUrl } = useJobApplications();

  const [activeFilter, setActiveFilter] = useState<FilterType>('All');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<string | undefined>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | undefined>('descend');

  const params = useMemo(() => ({
    page,
    limit,
    search,
    department: activeFilter !== 'All' ? activeFilter : undefined,
    orderBy: sortField,
    orderType: (sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined) as 'asc' | 'desc' | undefined,
  }), [page, limit, search, activeFilter, sortField, sortOrder]);

  const { data, isLoading, refetch } = getJobApplicationsWithFilters(params);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      refetch();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [params, refetch]);

  const applications = (data?.data || []) as EnrichedJobApplication[];
  const total = data?.pagination?.totalCount || 0;

  const handleTableChange = (_pagination: TablePaginationConfig, _filters: any, sorter: any) => {
    if (Array.isArray(sorter)) {
      // Multiple sorters (not used in this case)
      return;
    }
    
    if (sorter && sorter.field) {
      const field = sorter.field as string;
      const order = sorter.order === 'ascend' ? 'ascend' : sorter.order === 'descend' ? 'descend' : undefined;
      setSortField(field);
      setSortOrder(order);
      setPage(1); // Reset to first page when sorting changes
    } else {
      setSortField('updatedAt');
      setSortOrder('descend');
      setPage(1);
    }
  };

  const handleStatusChange = (id: string, status: ApplicationStatus) => {
    updateJobApplicationStatus.mutate({ id, data: { status } });
  };

  const columns: TableColumnsType<EnrichedJobApplication> = [
    {
      title: <span className="font-azonix">CANDIDATE</span>,
      key: 'candidate',
      dataIndex: 'fullName',
      render: (_: any, record) => (
        <div>
          <div className="font-coda text-white">{record.fullName}</div>
          <div className="font-coda text-secondary text-sm">{record.email}</div>
        </div>
      ),
      sorter: true,
    },
    {
      title: <span className="font-azonix">POSITION</span>,
      key: 'position',
      dataIndex: ['position', 'title'],
      render: (title: string) => <span className="font-coda">{title}</span>,
      sorter: true,
    },
    {
      title: <span className="font-azonix">DEPARTMENT</span>,
      key: 'department',
      dataIndex: ['position', 'department'],
      render: (department: string) => <span className="font-coda">{department}</span>,
      sorter: true,
    },
    {
      title: <span className="font-azonix">STATUS</span>,
      key: 'status',
      dataIndex: 'status',
      render: (status: ApplicationStatus, record) => (
        <Dropdown
          menu={{
            items: [
              { key: 'pending', label: 'Pending' },
              { key: 'reviewed', label: 'Reviewed' },
              { key: 'contacted', label: 'Contacted' },
              { key: 'rejected', label: 'Rejected' },
              { key: 'hired', label: 'Hired' },
            ].map(item => ({
              key: item.key,
              label: (
                <span className="font-coda" onClick={() => handleStatusChange(record.id, item.key as ApplicationStatus)}>
                  {item.label}
                </span>
              ),
              className: status === item.key ? 'ant-dropdown-menu-item-selected' : ''
            })),
          }}
          trigger={['click']}
        >
          <Button type="text" className="font-coda !font-normal uppercase text-primary">
            {status}
          </Button>
        </Dropdown>
      ),
      sorter: true,
    },
    {
      title: <span className="font-azonix">APPLIED AT</span>,
      key: 'createdAt',
      dataIndex: 'createdAt',
      render: (date: string) => <span className="font-coda">{date ? dayjs(date).format('MM/DD/YYYY') : '-'}</span>,
      sorter: true,
    },
    {
      title: <span className="font-azonix">ACTIONS</span>,
      key: 'actions',
      width: 150,
      render: (_: any, record) => (
        <div className="flex gap-2">
          {record.resume && (
            <Tooltip title="Download Resume">
              <Button
                type="text"
                icon={<DownloadOutlined className="!text-xl"/>}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(getResumeUrl(record.resume!), '_blank');
                }}
                className="text-primary hover:[&_svg]:transition-all hover:[&_svg]:duration-200 hover:[&_svg]:scale-110 hover:[&_svg]:drop-shadow-[0_4px_8px_#00ade6]"
              />
            </Tooltip>
          )}
          <Tooltip title="Delete Application">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined className="!text-lg"/>}
              onClick={(e) => {
                e.stopPropagation();
                deleteJobApplication.mutate(record.id);
              }}
              className="!text-red-500 hover:[&_svg]:transition-all hover:[&_svg]:duration-200 hover:[&_svg]:scale-110 hover:[&_svg]:drop-shadow-[0_4px_8px_#ef4444]"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="estabrook-bg pt-25 pb-10 md:pb-20">
      <section className="w-full h-[400px]">
        <div className="container h-full flex flex-col justify-center items-center mx-auto">
          <Title level={1} className="font-azonix !font-normal text-primary text-center tracking-wider pb-4 !mb-0"
          >
            TALENT POOL
          </Title>
          <div className="max-w-3xl mx-auto !mb-8">
            <Paragraph className="text-center text-lg text-light">
              View and manage all job applications
            </Paragraph>
          </div>
        </div>
      </section>

      <section className="w-full py-12 bg-charcoal">
        <div className="container mx-auto px-4">
          <div>
            <Title level={2} className="font-azonix !font-normal text-white">ALL APPLICATIONS</Title>
          </div>
          <Paragraph className="text-white !mb-8">
            Filter applications by department or search by candidate name.
          </Paragraph>

          {/* Department Filters */}
          <div className="flex flex-wrap justify-start gap-2 mb-6">
            {(['All', 'Operations', 'Management', 'Snow Removal', 'Maintenance', 'Administration'] as FilterType[]).map((filter) => (
              <Button
                key={filter}
                type="default"
                className={`filter-button ${activeFilter === filter ? 'filter-active' : ''}`}
                onClick={() => setActiveFilter(filter)}
              >
                {filter}
              </Button>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              {/* Custom Search Input */}
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <SearchOutlined className="!text-[#00ade6]" style={{ color: '#00ade6' }} />
                </div>
                <input
                  type="text"
                  placeholder="Search applications..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="w-full sm:w-72 h-11 pl-11 pr-4 bg-zinc-800/80 border border-zinc-700 text-white placeholder-zinc-500 font-coda text-sm focus:outline-none focus:border-[#00ade6] focus:ring-1 focus:ring-[#00ade6]/50 transition-all duration-200"
                />
                {search && (
                  <button
                    onClick={() => { setSearch(''); setPage(1); }}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-zinc-500 hover:text-white transition-colors cursor-pointer"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Applications Table */}
          <div className="bg-zinc-900/50 border border-zinc-800 shadow-xl overflow-x-auto hide-scrollbar">
            <Table<EnrichedJobApplication>
              columns={columns}
              dataSource={applications}
              rowKey={(record) => record.id}
              loading={isLoading}
              onChange={handleTableChange}
              pagination={{
                current: page,
                pageSize: limit,
                total: total,
                onChange: (currentPage, pageSize) => {
                  setPage(currentPage);
                  setLimit(pageSize);
                },
                showSizeChanger: true,
                showTotal: (total) => <span className="text-white font-coda">Total <span className="text-white font-coda">{total}</span> applications</span>,
                className: "!bg-transparent [&_.ant-pagination-item]:!bg-zinc-800 [&_.ant-pagination-item]:!border-zinc-700 [&_.ant-pagination-item-active]:!bg-transparent [&_.ant-pagination-item-active]:!border-zinc-700 [&_.ant-pagination-item_a]:!text-white [&_.ant-pagination-item-active_a]:!text-white [&_.ant-pagination-prev_.ant-pagination-item-link]:!bg-zinc-800 [&_.ant-pagination-next_.ant-pagination-item-link]:!bg-zinc-800 [&_.ant-pagination-prev_.ant-pagination-item-link]:!border-zinc-700 [&_.ant-pagination-next_.ant-pagination-item-link]:!border-zinc-700 [&_.ant-pagination-prev_.ant-pagination-item-link]:!text-white [&_.ant-pagination-next_.ant-pagination-item-link]:!text-white",
              }}
              className="[&_.ant-table]:!bg-transparent [&_.ant-table-thead_th]:!bg-zinc-800/50 [&_.ant-table-thead_th]:!text-white [&_.ant-table-thead_th]:!border-b-[#00ade6] [&_.ant-table-thead_th]:!font-azonix [&_.ant-table-tbody_td]:!bg-transparent [&_.ant-table-tbody_td]:!border-b-zinc-700 [&_.ant-table-tbody_td]:!text-white [&_.ant-table-row:hover_td]:!bg-zinc-800/50 [&_.ant-table-placeholder]:!bg-transparent [&_.ant-empty-description]:!text-white min-w-[800px]"
            />
          </div>
        </div>
      </section>
    </div>
  );
};
